import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AccountBalanceWallet,
  Diamond,
  Receipt,
  TrendingUp,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';
import ResponsiveDataGrid from '../components/ResponsiveDataGrid';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import { CategoryIndicator, CategoryOptionContent, CATEGORY_VALUES, categoryLabel, categoryAccent } from '../components/Indicators';
import { ACCENTS, tint } from '../theme/accents';
import { formatINR } from '../utils/currency';
import { MethodOptionContent, METHOD_VALUES } from '../components/Indicators';

// Each category owns a hue, so a glance down the table reads as a breakdown

// Metal purchases capture how much was bought and at what rate
const METAL_CATEGORIES = ['gold', 'silver'];

const emptyForm = {
  category: 'gold',
  description: '',
  amount: '',
  expenseDate: dayjs().format('YYYY-MM-DD'),
  quantity: '',
  unit: 'gram',
  ratePerUnit: '',
  vendor: '',
  paymentMethod: 'UPI',
  notes: '',
};

// DataGrid renders this instead of a bare "No rows" line.
const NoRows = () => (
  <EmptyState
    icon={<Receipt />}
    accent={ACCENTS.marigold}
    title="No expenses recorded"
    description="Anything the fund spends — gold, silver, meeting costs — is listed here with its quantity and rate."
  />
);

const Expenses = () => {
  const { isAdmin } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [holdings, setHoldings] = useState(null);
  const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
  const [rateForm, setRateForm] = useState({ goldRatePerGram: '', silverRatePerGram: '' });

  useEffect(() => {
    if (ratesDialogOpen && holdings) {
      const rateOf = (c) => holdings.holdings.find(h => h.category === c)?.currentRate || '';
      setRateForm({ goldRatePerGram: rateOf('gold') || '', silverRatePerGram: rateOf('silver') || '' });
    }
  }, [ratesDialogOpen, holdings]);

  const handleSaveRates = async () => {
    try {
      const payload = {};
      if (rateForm.goldRatePerGram !== '') payload.goldRatePerGram = parseFloat(rateForm.goldRatePerGram);
      if (rateForm.silverRatePerGram !== '') payload.silverRatePerGram = parseFloat(rateForm.silverRatePerGram);

      if (Object.keys(payload).length === 0) {
        toast.error('Enter at least one rate');
        return;
      }

      await axios.put('/api/expenses/rates', payload);
      toast.success('Rates updated');
      setRatesDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update rates');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [expensesRes, summaryRes, statsRes, holdingsRes] = await Promise.all([
        axios.get(`/api/expenses${categoryFilter ? `?category=${categoryFilter}` : ''}`),
        axios.get('/api/expenses/summary'),
        axios.get('/api/dashboard/stats'),
        axios.get('/api/expenses/holdings'),
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setHoldings(holdingsRes.data);
      setBalance({
        collected: Number(statsRes.data.totalFund) || 0,
        spent: Number(statsRes.data.totalExpenses) || 0,
        available: Number(statsRes.data.availableBalance) || 0,
      });
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isMetal = METAL_CATEGORIES.includes(form.category);

  // Keep amount in step with quantity x rate so the two cannot disagree
  const handleQuantityOrRate = (field, value) => {
    const next = { ...form, [field]: value };
    const qty = parseFloat(field === 'quantity' ? value : form.quantity);
    const rate = parseFloat(field === 'ratePerUnit' ? value : form.ratePerUnit);
    if (Number.isFinite(qty) && Number.isFinite(rate)) {
      next.amount = (qty * rate).toFixed(2);
    }
    setForm(next);
  };

  // Same shape as every other list in the app: a DataGrid with the shared
  // recipe, so Expenses sorts, paginates and opens on row click like Payments.
  const columns = [
    {
      field: 'expense_date',
      headerName: 'Date',
      width: 105,
      renderCell: (params) => dayjs(params.value).format('D MMM YYYY'),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 115,
      renderCell: (params) => <CategoryIndicator category={params.value} size="small" />,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.8,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {params.value}
          </Typography>
          {params.row.vendor && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {params.row.vendor}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 95,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (params.value ? `${Number(params.value)} ${params.row.unit || ''}` : '—'),
    },
    {
      field: 'rate_per_unit',
      headerName: 'Rate',
      width: 105,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (params.value ? formatINR(params.value) : '—'),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" className="tabular" sx={{ fontWeight: 700 }}>
          {formatINR(params.value)}
        </Typography>
      ),
    },
    ...(isAdmin ? [{
      field: 'actions',
      headerName: '',
      width: 84,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEdit(params.row); }}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(params.row); }}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    }] : []),
  ];

  const handleOpenCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  // Reads are open to every member (the group's transparency rule), so a row
  // opens the same dialog for everyone — read-only unless you can edit it.
  const handleOpenDetail = (expense) => {
    handleOpenEdit(expense);
  };

  const handleOpenEdit = (expense) => {
    setEditing(expense);
    setForm({
      category: expense.category,
      description: expense.description,
      amount: expense.amount,
      expenseDate: dayjs(expense.expense_date).format('YYYY-MM-DD'),
      quantity: expense.quantity ?? '',
      unit: expense.unit ?? 'gram',
      ratePerUnit: expense.rate_per_unit ?? '',
      vendor: expense.vendor ?? '',
      paymentMethod: expense.payment_method ?? '',
      notes: expense.notes ?? '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const payload = {
      category: form.category,
      description: form.description.trim(),
      amount: parseFloat(form.amount),
      expenseDate: form.expenseDate,
      quantity: isMetal && form.quantity ? parseFloat(form.quantity) : null,
      unit: isMetal ? form.unit : null,
      ratePerUnit: isMetal && form.ratePerUnit ? parseFloat(form.ratePerUnit) : null,
      vendor: form.vendor || null,
      paymentMethod: form.paymentMethod || null,
      notes: form.notes || null,
    };

    try {
      if (editing) {
        await axios.put(`/api/expenses/${editing.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post('/api/expenses', payload);
        toast.success('Expense recorded');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.errors?.[0]?.msg || error.response?.data?.error;
      toast.error(detail || 'Failed to save expense');
    }
  };

  const handleDelete = async (expense) => {
    try {
      await axios.delete(`/api/expenses/${expense.id}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const metalHoldings = (summary?.byCategory || []).filter(
    row => METAL_CATEGORIES.includes(row.category) && Number(row.total_quantity) > 0
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Money out"
        accent={ACCENTS.marigold}
        title="Expenses"
        subtitle="What the fund has bought, and what it holds as a result."
        actions={isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            Record Expense
          </Button>
        )}
      />

      {/* Where the fund stands once spending is taken off */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={6} sm={4}>
          <StatCard title="Collected" value={formatINR(balance?.collected)} subtitle="All time"
            icon={<AccountBalanceWallet />} accent={ACCENTS.emerald} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard title="Spent" value={formatINR(balance?.spent)} subtitle="On agreed purchases"
            icon={<Receipt />} accent={ACCENTS.vermilion} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Balance" value={formatINR(balance?.available)} subtitle="Left to spend"
            icon={<Diamond />} accent={(balance?.available || 0) < 0 ? ACCENTS.vermilion : ACCENTS.indigo} />
        </Grid>
      </Grid>

      {/* What the metal is worth now, against what it cost */}
      {holdings?.holdings?.length > 0 && (
        <Paper sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
            <Typography variant="overline" sx={{ color: ACCENTS.gold }}>
              <Diamond fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Metal held by the fund
            </Typography>
            {isAdmin && (
              <Button size="small" startIcon={<TrendingUp />} onClick={() => setRatesDialogOpen(true)}>
                {holdings.ratesSet ? 'Update rates' : 'Set current rates'}
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            {holdings.holdings.map(row => {
              const accent = categoryAccent(row.category);
              const up = row.gain !== null && row.gain >= 0;
              return (
                <Grid item xs={12} sm={6} md={4} key={row.category}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: tint(accent, 0.3), bgcolor: tint(accent, 0.07) }}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between" mb={0.5}>
                      <Typography className="tabular" sx={{ fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.5rem', color: accent }}>
                        {Number(row.quantity).toLocaleString('en-IN')} {row.unit}
                      </Typography>
                      <CategoryIndicator category={row.category} size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cost {formatINR(row.cost)} · avg {formatINR(Math.round(row.averageRate))}/{row.unit}
                    </Typography>

                    {row.currentValue === null ? (
                      <Typography variant="caption" sx={{ color: ACCENTS.marigold, mt: 1, display: 'block' }}>
                        Set a current rate to see what this is worth
                      </Typography>
                    ) : (
                      <Box mt={1.25}>
                        <Typography className="tabular" variant="h6" sx={{ lineHeight: 1.2 }}>
                          {formatINR(Math.round(row.currentValue))}
                        </Typography>
                        <Typography
                          variant="caption"
                          className="tabular"
                          sx={{ color: up ? ACCENTS.emerald : ACCENTS.vermilion, fontWeight: 700 }}
                        >
                          {up ? '▲' : '▼'} {formatINR(Math.abs(Math.round(row.gain)))} ({row.gainPercent >= 0 ? '+' : ''}{row.gainPercent.toFixed(2)}%)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          at {formatINR(row.currentRate)}/{row.unit}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {holdings.ratesUpdatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Rates last updated {dayjs(holdings.ratesUpdatedAt).format('D MMM YYYY, h:mm A')}
            </Typography>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All categories</MenuItem>
          {CATEGORY_VALUES.map(c => (
            <MenuItem key={c} value={c}><CategoryOptionContent category={c} /></MenuItem>
          ))}
        </TextField>
      </Paper>

      <Paper sx={{ width: '100%', border: '1px solid', borderColor: 'divider' }}>
        <ResponsiveDataGrid
          mobile={{ title: 'description', amount: 'amount' }}
          rows={expenses}
          columns={columns}
          getRowId={(row) => row.id}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onRowClick={(params) => handleOpenDetail(params.row)}
          autoHeight
          loading={loading}
          slots={{ noRowsOverlay: NoRows }}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            // v6 collapses the no-rows overlay under autoHeight, clipping it
            '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
            '& .MuiDataGrid-overlayWrapperInner': { minHeight: 300 },
            '& .MuiDataGrid-cell:hover': { color: 'primary.main' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{!isAdmin ? 'Expense' : editing ? 'Edit Expense' : 'Record Expense'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                disabled={!isAdmin}
                select
                fullWidth
                label="Category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORY_VALUES.map(c => (
                  <MenuItem key={c} value={c}><CategoryOptionContent category={c} /></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                disabled={!isAdmin}
                fullWidth
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={form.expenseDate}
                onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                disabled={!isAdmin}
                fullWidth
                label="Description"
                placeholder="e.g. Silver coins purchased at meeting"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
              />
            </Grid>

            {isMetal && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    disabled={!isAdmin}
                    fullWidth
                    type="number"
                    label="Quantity"
                    value={form.quantity}
                    onChange={(e) => handleQuantityOrRate('quantity', e.target.value)}
                    inputProps={{ min: 0, step: 0.001 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    disabled={!isAdmin}
                    select
                    fullWidth
                    label="Unit"
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  >
                    <MenuItem value="gram">gram</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="piece">piece</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    disabled={!isAdmin}
                    fullWidth
                    type="number"
                    label="Rate per unit"
                    value={form.ratePerUnit}
                    onChange={(e) => handleQuantityOrRate('ratePerUnit', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                disabled={!isAdmin}
                fullWidth
                type="number"
                label="Total amount"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                required
                helperText={isMetal ? 'Auto-filled from quantity × rate; editable' : ' '}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                disabled={!isAdmin}
                select
                fullWidth
                label="Paid via"
                value={form.paymentMethod}
                onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
              >
                {METHOD_VALUES.map(m => (
                  <MenuItem key={m} value={m}><MethodOptionContent method={m} /></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                disabled={!isAdmin}
                fullWidth
                label="Vendor / shop"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                disabled={!isAdmin}
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{isAdmin ? 'Cancel' : 'Close'}</Button>
          {isAdmin && (
            <Button variant="contained" onClick={handleSubmit}>
              {editing ? 'Update' : 'Record'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Today's market rate, entered by an admin - the group buys rarely, so a
          manual figure is more dependable than a price feed that can go stale */}
      <Dialog open={ratesDialogOpen} onClose={() => setRatesDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Current metal rates</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter today's market rate per gram. The fund's holdings are valued against these.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Gold rate per gram"
                value={rateForm.goldRatePerGram}
                onChange={(e) => setRateForm({ ...rateForm, goldRatePerGram: e.target.value })}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Silver rate per gram"
                value={rateForm.silverRatePerGram}
                onChange={(e) => setRateForm({ ...rateForm, silverRatePerGram: e.target.value })}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatesDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRates}>Save rates</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;
